import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { DollarSign, BookOpen, CalendarCheck, Gift } from "lucide-react";

const money = (n) => `$${Number(n).toFixed(2)}`;
const PERIODS = [["today", "Today"], ["week", "Last 7 days"], ["month", "Last 30 days"]];
const KIND = { toolkits: "Toolkits", session: "Session", gift: "Gift" };

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="card-surface p-5" data-testid={testid}>
    <div className="text-xs text-neutral-500 inline-flex items-center gap-2 mb-2"><Icon size={13} className="text-teal-300" /> {label}</div>
    <div className="font-display text-3xl text-neutral-100">{value}</div>
  </div>
);

export default function AdminSales() {
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [period, setPeriod] = useState("today");
  useEffect(() => { api.get("/admin/sales").then(({ data }) => setD(data)).catch(() => setDenied(true)); }, []);

  if (denied) return <div className="p-12 text-center text-neutral-400" data-testid="admin-sales-denied">Admin access required.</div>;
  if (!d) return <div className="p-12 text-center text-teal-200/70">Loading…</div>;
  const s = d[period];

  return (
    <div className="max-w-5xl mx-auto px-6 pt-12 pb-28 space-y-6" data-testid="admin-sales-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-teal-300/80 mb-2">Admin · Sales</div>
          <h1 className="font-display text-4xl text-neutral-100">How it's going</h1>
          <p className="text-sm text-neutral-400 mt-2">{d.upcoming_sessions} upcoming session{d.upcoming_sessions === 1 ? "" : "s"} · {d.gifts_unredeemed} gift{d.gifts_unredeemed === 1 ? "" : "s"} not yet redeemed</p>
        </div>
        <div className="inline-flex rounded-full border border-white/10 p-1 bg-black/30" role="tablist">
          {PERIODS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={period === k} onClick={() => setPeriod(k)} data-testid={`sales-period-${k}`}
              className={`rounded-full px-4 py-1.5 text-xs transition-colors ${period === k ? "bg-teal-400 text-black" : "text-neutral-300 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={DollarSign} label="Revenue" value={money(s.revenue)} testid="sales-revenue" />
        <Stat icon={BookOpen} label={`Toolkit orders (${s.toolkits_sold} toolkits)`} value={s.toolkit_orders} testid="sales-toolkits" />
        <Stat icon={CalendarCheck} label="Sessions booked" value={s.sessions_booked} testid="sales-sessions" />
        <Stat icon={Gift} label="Gifts sent" value={s.gifts_sent} testid="sales-gifts" />
      </div>
      <div className="card-surface p-6" data-testid="sales-recent">
        <h2 className="font-display text-2xl mb-4">Recent activity</h2>
        {d.recent.length === 0 ? <p className="text-sm text-neutral-500">No orders yet. They'll show up here.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-neutral-500"><th className="py-2 pr-4">When</th><th className="pr-4">Type</th><th className="pr-4">Who</th><th className="pr-4">What</th><th className="text-right">Amount</th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {d.recent.map((o, i) => (
                  <tr key={i} data-testid={`sales-row-${i}`}>
                    <td className="py-2.5 pr-4 text-neutral-400 whitespace-nowrap">{new Date(o.when).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                    <td className="pr-4 text-neutral-300">{KIND[o.kind]}</td>
                    <td className="pr-4 text-neutral-300">{o.name || o.email}</td>
                    <td className="pr-4 text-neutral-400">{o.what}</td>
                    <td className="text-right text-neutral-100">{o.amount ? money(o.amount) : "Gift"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
